import { ColumnDef } from '@tanstack/react-table'
import { MoreHorizontal, Edit, Trash2, Eye, Play, Pause, RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Badge } from '@/components/ui/badge'
import { TranslatableText } from '@/components/ui/TranslatableText'
import { formatRelativeTime } from '@/lib/utils'
import type { Job } from '@/types/entities'

interface JobActionsProps {
    job: Job
    onEdit: (job: Job) => void
    onDelete: (job: Job) => void
    onView: (job: Job) => void
    onRun: (job: Job) => void
    onPause: (job: Job) => void
    onResume: (job: Job) => void
}

function JobActions({ job, onEdit, onDelete, onView, onRun, onPause, onResume }: JobActionsProps) {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0">
                    <span className="sr-only">Open menu</span>
                    <MoreHorizontal className="h-4 w-4" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onView(job)}>
                    <Eye className="mr-2 h-4 w-4" />
                    View
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onEdit(job)}>
                    <Edit className="mr-2 h-4 w-4" />
                    Edit
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onRun(job)}>
                    <Play className="mr-2 h-4 w-4" />
                    Run Now
                </DropdownMenuItem>
                {job.status === 'running' ? (
                    <DropdownMenuItem onClick={() => onPause(job)}>
                        <Pause className="mr-2 h-4 w-4" />
                        Pause
                    </DropdownMenuItem>
                ) : job.status === 'paused' ? (
                    <DropdownMenuItem onClick={() => onResume(job)}>
                        <RotateCcw className="mr-2 h-4 w-4" />
                        Resume
                    </DropdownMenuItem>
                ) : null}
                <DropdownMenuItem
                    onClick={() => onDelete(job)}
                    className="text-destructive"
                >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    )
}

export function createJobColumns(
    onEdit: (job: Job) => void,
    onDelete: (job: Job) => void,
    onView: (job: Job) => void,
    onRun: (job: Job) => void,
    onPause: (job: Job) => void,
    onResume: (job: Job) => void,
    t: (key: string) => string
): ColumnDef<Job>[] {
    return [
        {
            accessorKey: 'name',
            header: 'Name',
            cell: ({ row }) => {
                const job = row.original
                return (
                    <div className="font-medium">
                        <TranslatableText text={job.name} />
                    </div>
                )
            },
        },
        {
            accessorKey: 'schedule',
            header: 'Schedule',
            cell: ({ row }) => {
                const schedule = row.getValue('schedule') as string
                return (
                    <code className="text-sm bg-muted px-2 py-1 rounded">
                        {schedule}
                    </code>
                )
            },
        },
        {
            accessorKey: 'policy',
            header: 'Policy',
            cell: ({ row }) => {
                const job = row.original
                return (
                    <div className="text-sm">
                        {job.policy?.name ? <TranslatableText text={job.policy.name} /> : `Policy #${job.policyId}`}
                    </div>
                )
            },
        },
        {
            accessorKey: 'target',
            header: 'Target',
            cell: ({ row }) => {
                const job = row.original
                return (
                    <div className="text-sm">
                        {job.target?.name ? <TranslatableText text={job.target.name} /> : `Target #${job.targetId}`}
                    </div>
                )
            },
        },
        {
            accessorKey: 'status',
            header: 'Status',
            cell: ({ row }) => {
                const status = row.getValue('status') as string
                const statusColors = {
                    idle: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300',
                    running: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
                    completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
                    failed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
                    paused: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
                }
                return (
                    <Badge className={statusColors[status as keyof typeof statusColors] || 'bg-gray-100 text-gray-800'}>
                        {status}
                    </Badge>
                )
            },
        },
        {
            accessorKey: 'enabled',
            header: 'Enabled',
            cell: ({ row }) => {
                const enabled = row.getValue('enabled') as boolean
                return (
                    <Badge variant={enabled ? 'default' : 'secondary'}>
                        {enabled ? 'Yes' : 'No'}
                    </Badge>
                )
            },
        },
        {
            accessorKey: 'lastRun',
            header: 'Last Run',
            cell: ({ row }) => {
                const lastRun = row.getValue('lastRun') as string | undefined
                return (
                    <div className="text-sm text-muted-foreground">
                        {lastRun ? formatRelativeTime(lastRun) : 'Never'}
                    </div>
                )
            },
        },
        {
            accessorKey: 'nextRun',
            header: 'Next Run',
            cell: ({ row }) => {
                const nextRun = row.getValue('nextRun') as string | undefined
                return (
                    <div className="text-sm text-muted-foreground">
                        {nextRun ? formatRelativeTime(nextRun) : 'Not scheduled'}
                    </div>
                )
            },
        },
        {
            id: 'actions',
            header: t('common.actions'),
            cell: ({ row }) => {
                const job = row.original
                return (
                    <JobActions
                        job={job}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        onView={onView}
                        onRun={onRun}
                        onPause={onPause}
                        onResume={onResume}
                    />
                )
            },
        },
    ]
}



